import { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiInstance from '../../api';
import { message, notification } from 'antd';
import ActionBar from './components/ActionBar';
import Loader from '../../components/Loader';
import './styles.css';
import { useReactToPrint } from 'react-to-print';
import ExcelJS from 'exceljs';
import moment from 'moment';
import { useSelector } from 'react-redux';

const ProductionOrderPage = () => {
    const [searchParams] = useSearchParams();
    const { serverTime } = useSelector((store) => store.common); // "2025-07-04 10:40:46"
    const accessToken = searchParams.get('accessToken');
    const kitchenId = searchParams.get('kitchenId');
    const kitchenRacePolygonId = searchParams.get('kitchenRacePolygonId');
    const deliveryDay = searchParams.get('deliveryDay');
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState(null);
    const [products, setProducts] = useState([]);
    const [kitchenData, setKitchenData] = useState({});
    const [groupedProducts, setGroupedProducts] = useState({});

    useEffect(() => {
        if (data) {
            setProducts(receivingProducts(data));
            setKitchenData(receivingKitchenData(data));
        }
    }, [data]);

    useEffect(() => {
        if (deliveryDay) {
        const serverDate = serverTime ? moment(serverTime).format('YYYY-MM-DD') : null;
        const clientDate = moment().format('YYYY-MM-DD');
        const referenceDate = serverDate || clientDate;

        if (deliveryDay !== referenceDate) {
            notification.warning({
                message: 'Внимание',
                description: `Вы смотрите заказы не на сегодня, а на ${deliveryDay}. Текущая дата: ${serverDate || clientDate}`,
                duration: 0,
            });
        }
        }
    }, []);

    const fetchData = async (endpoint, data) => {
        const api = `${endpoint}?XDEBUG_SESSION_START=MyVSCode`;
        
        try {
            const response = await apiInstance.post(api, JSON.stringify({ data }));
            
            if (response.data.result) {
                return response.data.data;
            }
            
            message.error('Ошибка извлечения данных');
            return null;
        } catch (error) {
            message.error(error.response?.data?.message || 'Ошибка сервера');
            return null;
        }
    };

    // --- Вспомогательные функции для форматирования информации ---
    const formatProductionOrder = (kitchenData, kitchenId) => kitchenData.kitchenName || kitchenId || '-';

    const formatDeliveryTime = (kitchenData, kitchenRacePolygonId, deliveryDay) => {
        if (kitchenData.timeStart && kitchenData.timeEnd && kitchenData.timezone != null) {
            const start = moment(kitchenData.timeStart, 'HH:mm').add(kitchenData.timezone, 'hour').format('HH:mm');
            const end = moment(kitchenData.timeEnd, 'HH:mm').add(kitchenData.timezone, 'hour').format('HH:mm');
            const date = deliveryDay ? moment(deliveryDay).format('DD-MM-YYYY') : '';
            return `${start}-${end} ${date}`;
        }
        if (kitchenRacePolygonId || deliveryDay) {
            return `${kitchenRacePolygonId || '-'} ${deliveryDay ? moment(deliveryDay).format('DD-MM-YYYY') : ''}`;
        }
        return '-';
    };

    const formatDeliveryZones = (kitchenData) => kitchenData.cityName || '-';

    const formatShutoffTime = (kitchenData) => {
        if (kitchenData.shutoff && kitchenData.timezone != null) {
            return moment(kitchenData.shutoff, 'HH:mm').add(kitchenData.timezone, 'hour').format('HH:mm');
        }
        return '-';
    };

    // Возвращает массив объектов с данными о кухне из массива data
    const receivingKitchenData = (data) => {
        if (!Array.isArray(data) || data.length === 0) return {};
        const item = data[0];
        if (!item) return {};
        return {
            deliveryDay: item.deliveryDay,
            kitchenName: item.kitchenName,
            kitchenId: item.kitchenId,
            cityName: item.citiName,
            addressId: item.addressId,
            kitchenRacePolygonId: item.kitchenRacePolygonId,
            timeStart: item.timeStart,
            timeEnd: item.timeEnd,
            shutoff: item.timeTransferringOrderToCourier,
            timezone: item.timezone
        };
    }

    // Возвращает плоский массив всех продуктов из массива data
    const receivingProducts = (data) => {
        if (!Array.isArray(data)) return [];
        return data.flatMap(item => Array.isArray(item.products) ? item.products : []);
    }

    const groupProductsByCategory = (items) => {
        const grouped = items.reduce((acc, item) => {
            const key = item.categoryId ?? item.categoryName ?? 'Без категории';
            if (!acc[key]) {
                acc[key] = {
                    categoryName: item.categoryName || 'Без категории',
                    categorySort: item.categorySort || null,
                    items: [],
                };
            }
            acc[key].items.push(item);
            return acc;
        }, {});

        // Сортировка внутри каждой категории по названию
        Object.values(grouped).forEach(group => {
            group.items.sort((a, b) => {
                const nameA = (a.name || '').toLowerCase();
                const nameB = (b.name || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
        });
        return grouped;
    };

    const processProducts = (products) => {
        const result = [];
        const productMap = new Map();

        for (const product of products) {
            if (product.orderProductId === null) {
                const productWithModificator = { ...product, modificator: null };
                result.push(productWithModificator);
                productMap.set(product.id, productWithModificator);
            } else {
                const parentProduct = productMap.get(product.orderProductId);
                if (parentProduct) {
                    parentProduct.modificator = product;
                }
            }
        }

        return result;
    };

    const aggregateProductionItems = (combinedProducts) => {
        const map = new Map();

        combinedProducts.forEach((base) => {
            const modifier = base.modificator;
            const modifierKey = modifier ? (modifier.productId ?? modifier.name ?? 'mod') : 'base';
            const key = `${base.productId}|${base.price}|${modifierKey}`;

            if (!map.has(key)) {
                map.set(key, {
                    productId: base.productId,
                    price: base.price,
                    name: base.name,
                    categoryId: base.categoryId,
                    categoryName: base.categoryName,
                    categorySort: base.categorySort,
                    modifierName: modifier ? (modifier.name || 'Без названия') : null,
                    totalSold: 0,
                });
            }

            const amount = base.amount ?? 0;
            map.get(key).totalSold += amount;
        });

        return Array.from(map.values());
    };

    useEffect(() => {
        const loadData = async () => {
        setIsLoading(true);
            try {
                if (accessToken) {
                    const response = await fetchData(
                        '/documents/checklist/getOrdersByAccessToken',
                        { access_token: accessToken }
                    );
                    if (response) setData(response);
                } else if (kitchenId && kitchenRacePolygonId && deliveryDay) {
                    const response = await fetchData(
                        '/documents/checklist/getOrders',
                        {
                            kitchenId: kitchenId,
                            kitchenRacePolygonIds: [kitchenRacePolygonId],
                            deliveryDay: deliveryDay,
                        }
                    );
                    console.log('Fetched production order data:', response);
                    if (response) setData(response.orders);
                } else {
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error);
                setIsLoading(false);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [searchParams]);

    useEffect(() => {
        if (products && products.length) {
            const combined = processProducts(products);
            const aggregated = aggregateProductionItems(combined);
            setGroupedProducts(groupProductsByCategory(aggregated));
        } else {
            setGroupedProducts({});
        }
    }, [products]);

    const handleDownloadXLSX = async () => {
        if (!products.length || !Object.keys(groupedProducts).length) return;
        
        const rows = [
            [`Производственный заказ: ${formatProductionOrder(kitchenData, kitchenId)}`, ''],
            [`Доставка: ${formatDeliveryTime(kitchenData, kitchenRacePolygonId, kitchenData.deliveryDay || deliveryDay)}`, ''],
            [`Зоны доставки: ${formatDeliveryZones(kitchenData)}`, ''],
            [`Время передачи заказ в доставку: ${formatShutoffTime(kitchenData)}`, ''],
            ['', ''],
            ['Название', 'Продано'],
        ];

        const categoryRowIndexes = [];

        Object.values(groupedProducts).forEach(({ categoryName, items }) => {
            if (!items || !items.length) return;
            categoryRowIndexes.push(rows.length);
            rows.push([categoryName, '']);
            items.forEach((item) => {
                if (item.totalSold > 0) {
                    const label = item.modifierName ? `${item.name} + ${item.modifierName}` : item.name;
                    rows.push([label, item.totalSold]);
                }
            });
        });

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Production Order');
        worksheet.columns = [{ width: 80 }, { width: 12 }];

        const boldFont = { name: 'Calibri', bold: true };
        const highlightFill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F0E68C' },
        };

        const thinBorder = { style: 'thin', color: { argb: 'FF000000' } };

        rows.forEach((rowData, rowIdx) => {
            const row = worksheet.addRow(rowData);

            const isTableRow = rowIdx >= 5;

            // Форматирование первой колонки с поддержкой richText
            const cell1 = row.getCell(1);
            const text = rowData[0];
            if (typeof text === 'string' && text.includes('+')) {
                const plusIndex = text.indexOf('+');
                cell1.value = {
                    richText: [
                        { text: text.slice(0, plusIndex), font: { name: 'Calibri' } },
                        { text: text.slice(plusIndex), font: { name: 'Calibri', bold: true } }
                    ]
                };
            } else {
                cell1.value = text;
                cell1.font = { name: 'Calibri' };
            }


            // Категории — вся строка жирная и объединение ячеек 1 и 2
            if (categoryRowIndexes.includes(rowIdx)) {
                cell1.font = boldFont;
                // Объединить ячейки 1 и 2 в текущей строке
                worksheet.mergeCells(row.number, 1, row.number, 2);
            }

            if (rowIdx === 5) {
                row.font = boldFont;
            }

            row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                if (isTableRow) {
                    cell.border = {
                        top: thinBorder,
                        right: thinBorder,
                        bottom: thinBorder,
                        left: thinBorder,
                    };

                    // Не красим фон для строк категории
                    if (colNumber === 2 && !categoryRowIndexes.includes(rowIdx)) {
                        cell.fill = highlightFill;
                    }
                }
            });
        });

        const fileSuffix = kitchenData.deliveryDay ? `${moment(kitchenData.deliveryDay).format('DD-MM-YYYY')}` : deliveryDay ? `${moment(deliveryDay).format('DD-MM-YYYY')}` : '';
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const fileName = `Производственный заказ ${fileSuffix}.xlsx`;
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(link.href);
    };

    const handlePrint = useReactToPrint({
        content: () => document.getElementById('printable-area'),
        pageStyle: `
            @media print {
                @page { 
                    margin: 10mm;
                    size: A4 portrait !important;
                }
                
                body {
                    margin: 0;
                    padding: 0;
                    width: 100% !important;
                }
                
                #printable-area {
                    width: 100% !important;
                }
            }
        `
    });

    return (
        <div className="wrapper">
            {isLoading && <Loader/>}
                <ActionBar
                    onPrint={handlePrint}
                    onDownload={handleDownloadXLSX}
                    isDownloadDisabled={!groupedProducts}
                />
            {(!products || products.length === 0) && !isLoading ?
                <div className="checklist-no-data">Нет данных</div>
            :
                <div className="data-container" id="printable-area">

                    <div className="info-block">
                        <div className="info-row"><strong>Производственный заказ:</strong> {formatProductionOrder(kitchenData, kitchenId)}</div>
                        <div className="info-row"><strong>Доставка:</strong> {formatDeliveryTime(kitchenData, kitchenRacePolygonId, kitchenData.deliveryDay || deliveryDay)}</div>
                        <div className="info-row"><strong>Зоны доставки:</strong> {formatDeliveryZones(kitchenData)}</div>
                        <div className="info-row"><strong>Время передачи заказов в доставку:</strong> {formatShutoffTime(kitchenData)}</div>
                        <div className="info-row spacer" />
                    </div>

                    <table className="production-table">
                        <thead>
                            <tr>
                                <th className="table-header">Название</th>
                                <th className="table-header sold">Продано</th>
                            </tr>
                        </thead>
                        <tbody>
                            {console.log(groupedProducts)}
                            {Object.entries(groupedProducts)
                                .sort((a, b) => (a[1].categorySort || 0) - (b[1].categorySort || 0))
                                .map(([categoryKey, group]) => (
                                    group.items.length > 0 && (
                                        <Fragment key={categoryKey}>
                                            <tr className="category-row">
                                                <td colSpan={2}><strong>{group.categoryName}</strong></td>
                                            </tr>
                                            {group.items.map((item, idx) => (
                                                item.totalSold > 0 && (
                                                    <tr key={`${categoryKey}-${idx}`}>
                                                        <td>
                                                            {item.name}
                                                            {item.modifierName && <b> + {item.modifierName}</b>}
                                                        </td>
                                                        <td className="sold-cell">{Number(item.totalSold || 0)}</td>
                                                    </tr>
                                                )
                                            ))}
                                        </Fragment>
                                    )
                                ))
                            }
                        </tbody>
                    </table>
                </div>
            }
        </div>
    );
};

export default ProductionOrderPage;